import React, { useMemo } from 'react';

import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import { SalesChannelType } from '@fd/customHooks/useQueryRMSSalesChannels';
import { type Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  propertyManagerRolePermission,
  propertyOwnerRolePermission,
} from 'components/RMS/utils/permissions';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import PermissionsComponent from 'react-redux-permissions';
import { useHistory } from 'react-router-dom';

import PageLayout from '@fd/ui/Layout';
import LazyComponent from '@fd/ui/LazyComponent';
import Tabs from '@fd/ui/Tabs';

import { lazyWithRetry } from '../../../../../helpers/utilities';
import { notifyError, NotifyProps } from '../../../../../layouts/Notify/actions';
import type { Permissions } from '../../../../../reducers/root.reducer';
import { permissionsSelector } from '../../../../../selectors/permissions.selector';
import { flagService } from '../../../../../services/flagService/flagService';
import KioskDevicesTab from '../../../components/FlipdishDevicesTab';
import { Discovery } from '../../components/Discovery';
import { LegacyMobileSalesChannelsConfig } from '../../components/LegacyMobileSalesChannelsConfig';

const SalesChannelList = lazyWithRetry(
  () => import('../../../../RMS/SalesChannels/SalesChannelList')
);
const useStyles = makeStyles((theme: Theme) => ({
  container: {
    [theme.breakpoints.down('md')]: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
  },
}));

const MobileApps = (props: MappedState & MappedDispatch) => {
  const {
    hasAndroidApp,
    hasIosApp,
    translate,
    currentApp,
    selectedProperty,
    showSalesChannelsFlag,
    isFlipdishStaff,
    permissions,
  } = props;
  const classes = useStyles();
  const history = useHistory();
  const handleChannelClick = (salesChannel) => {
    history.push(
      `/${currentApp?.AppId}/sales-channels/mobile-apps/property/${selectedProperty?.code}/sales-channel/${salesChannel.salesChannelId}`
    );
  };

  const { data } = useQueryRMSCommonDataHook(currentApp?.AppId || '');
  const showSalesChannels = data.associations?.isMigratedToSalesChannels || showSalesChannelsFlag;

  const tabItems = useMemo(
    () =>
      [
        ...(showSalesChannels
          ? [
              {
                label: translate('Sales_Channels') as string,
                component: (
                  <LazyComponent>
                    <SalesChannelList
                      filterByType={[SalesChannelType.FLIPDISH_MOBILE_APP]}
                      onChannelClick={handleChannelClick}
                      hideFilter={false}
                    />
                  </LazyComponent>
                ),
                queryString: 'sales-channels',
              },
            ]
          : []),
        {
          label: translate('Devices') as string,
          component: (
            <LazyComponent>
              <KioskDevicesTab />
            </LazyComponent>
          ),
          queryString: 'devices',
          showForPermissions: ['Owner', 'ManagedOwner', 'FlipdishStaff'] as Permissions,
        },
        {
          label: translate('Settings') as string,
          component: <LegacyMobileSalesChannelsConfig />,
          queryString: 'settings',
          showForPermissions: ['Owner', 'ManagedOwner', 'FlipdishStaff'] as Permissions,
        },
      ].filter(
        (tab) =>
          !tab.showForPermissions ||
          tab.showForPermissions.some((permission) => (permissions || []).includes(permission))
      ),
    [showSalesChannels, translate, permissions, handleChannelClick]
  );
  const renderLegacyPage = () => {
    if (hasAndroidApp || hasIosApp || isFlipdishStaff) {
      return <Tabs tabItems={tabItems} />;
    } else {
      return <Discovery translate={translate} />;
    }
  };

  return (
    <PageLayout documentTitle="Mobile_apps" title={<Translate id="Mobile_apps" />}>
      <PermissionsComponent
        allowed={[
          'Owner',
          'ManagedOwner',
          'FlipdishStaff',
          ...propertyManagerRolePermission,
          ...propertyOwnerRolePermission,
        ]}
      >
        <div className={classes.container}>{renderLegacyPage()}</div>
      </PermissionsComponent>
    </PageLayout>
  );
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  notifyError: (data: NotifyProps) => dispatch(notifyError(data)),
});

const getPermissionsSelector = permissionsSelector.hasPermissionFactory(['FlipdishStaff']);
type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  const { currentApp, rms } = state;
  return {
    hasIosApp: currentApp.HasIosApp,
    hasAndroidApp: currentApp.HasAndroidApp,
    translate: getTranslate(state.locale),
    selectedProperty: rms.selectedProperty,
    showSalesChannelsFlag: flagService.isFlagOn(state, 'showSalesChannelStructure'),
    currentApp,
    isFlipdishStaff: getPermissionsSelector(state),
    permissions: state.permissions,
  };
};

const EnhancedComponent = connect(mapStateToProps, mapDispatchToProps)(MobileApps);
export { EnhancedComponent as MobileApps };
